import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { 
  Users, UserPlus, Flame, Check, Send, MapPin, Clock, Loader2, Video
} from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';
import { usePresence } from '../../context/PresenceContext';

export const BuddyList = ({ buddies, maxBuddies, nudgingBuddy, onNudge, onSwitchTab, onGoLive, goingLiveBuddyId }) => {
  const { onlineBuddyIds } = usePresence();
  return (
  <motion.div key="buddies" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
    <div className="text-center mb-6">
      <p className="text-muted-foreground">{buddies.length} / {maxBuddies} buddies{onlineBuddyIds.size > 0 && <span className="text-green-400 ml-1">· {onlineBuddyIds.size} online</span>}</p>
    </div>
    {buddies.length === 0 ? (
      <div className="text-center py-16 bg-card border border-border max-w-md mx-auto">
        <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-heading text-xl text-white mb-2">No Buddies Yet</h3>
        <p className="text-muted-foreground mb-4">Follow people and have them follow you back to add them as buddies!</p>
        <Link to="/social">
          <Button className="bg-primary text-black"><UserPlus className="h-4 w-4 mr-2" />Find People to Follow</Button>
        </Link>
      </div>
    ) : (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {buddies.map((buddy, i) => {
          const isOnline = onlineBuddyIds.has(buddy.id);
          return (
          <motion.div key={buddy.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-card border border-border p-6">
            <div className="flex items-start gap-4">
              <Link to={`/user/${buddy.id}`} className="relative">
                <Avatar className="h-14 w-14 border-2 border-border">
                  <AvatarImage src={getImageUrl(buddy.profile_picture)} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xl">{buddy.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                {isOnline && (
                  <span
                    className="absolute bottom-0 right-0 h-4 w-4 rounded-full bg-green-500 border-2 border-card ring-1 ring-green-300/40"
                    data-testid={`buddy-online-${buddy.id}`}
                    title="Online now"
                  />
                )}
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/user/${buddy.id}`} className="font-heading text-lg text-white hover:text-primary transition-colors">{buddy.name}</Link>
                  {isOnline && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/40">
                      Online
                    </span>
                  )}
                </div>
                {buddy.location?.city && (
                  <p className="text-muted-foreground text-sm flex items-center gap-1">
                    <MapPin className="h-3 w-3" />{buddy.location.city}{buddy.location.state ? `, ${buddy.location.state}` : ''}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-center">
              <div className="bg-black/50 p-3">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Flame className={`h-4 w-4 ${buddy.streak?.current_streak > 0 ? 'text-orange-400' : 'text-muted-foreground'}`} />
                  <span className="font-heading text-2xl text-white">{buddy.streak?.current_streak || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Current Streak</p>
              </div>
              <div className="bg-black/50 p-3">
                <div className="flex items-center justify-center gap-1 mb-1">
                  {buddy.streak?.worked_out_today ? <Check className="h-4 w-4 text-green-400" /> : <Clock className="h-4 w-4 text-yellow-400" />}
                  <span className={`font-heading text-sm ${buddy.streak?.worked_out_today ? 'text-green-400' : 'text-yellow-400'}`}>
                    {buddy.streak?.worked_out_today ? 'Done!' : 'Pending'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Today</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => onNudge(buddy.id)} disabled={nudgingBuddy === buddy.id} size="sm" className="flex-1 bg-primary text-black">
                {nudgingBuddy === buddy.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" />Nudge</>}
              </Button>
              <Link to={`/dm/${buddy.id}`} className="flex-1">
                <Button variant="outline" size="sm" className="w-full">Message</Button>
              </Link>
            </div>
            {onGoLive && (
              <Button
                onClick={() => onGoLive(buddy)}
                disabled={goingLiveBuddyId === buddy.id}
                size="sm"
                className="mt-2 w-full bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider"
                data-testid={`go-live-buddy-${buddy.id}`}
              >
                {goingLiveBuddyId === buddy.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <span className="inline-block h-2 w-2 rounded-full bg-white mr-1.5 animate-pulse" />
                    <Video className="h-4 w-4 mr-1.5" />
                    Go Live with {buddy.name?.split(' ')[0] || 'Buddy'}
                  </>
                )}
              </Button>
            )}
          </motion.div>
          );
        })}
      </div>
    )}
  </motion.div>
  );
};
